import type { ShortcutId } from "@/lib/shortcut-registry";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { guideHoldsSends, type GuideHoldState } from "./guide-send-hold";
import { GUIDE_SLIDES } from "./guide-slides";

/** Shortcuts the guide may hold back: the Denker surfaces it teaches and
 * their variants. Dictation anywhere types into other apps, never collides
 * with a lesson's keys, and stays available while the guide shows. */
export type GuideGatedShortcut = Exclude<
  ShortcutId,
  "shortcut.dictation.anywhere"
>;

/** A gated shortcut may act only in the lesson that teaches it. Read live
 * state at admission, including after awaits, rather than a render snapshot. */
export function guideAllowsShortcut(
  id: GuideGatedShortcut,
  state: GuideHoldState = useOnboardingStore.getState(),
): boolean {
  if (!guideHoldsSends(state)) return true;
  const story = state.contextualStory!;
  if (story.basicsStage === "opening") return false;
  const index = Math.max(
    0,
    Math.min(GUIDE_SLIDES.length - 1, story.guideSlide ?? 0),
  );
  return GUIDE_SLIDES[index]?.shortcut === id;
}

/** A pending shortcut belongs to the lesson that admitted it. Ending the
 * guide restores new shortcuts without reviving an earlier pending action. */
export function captureGuideShortcutGuard(
  id: GuideGatedShortcut,
): () => boolean {
  const state = useOnboardingStore.getState();
  const held = guideHoldsSends(state);
  const requestId = state.contextualStory?.requestId;
  const slide = state.contextualStory?.guideSlide ?? 0;
  return () => {
    const current = useOnboardingStore.getState();
    return (
      guideAllowsShortcut(id, current) &&
      held === guideHoldsSends(current) &&
      (!held ||
        (current.contextualStory?.requestId === requestId &&
          (current.contextualStory?.guideSlide ?? 0) === slide))
    );
  };
}
